import { ApiError, ProblemType } from '@/domains/api';
import { refusalMessage } from './refusal';

/**
 * A staff correction's answer, in words — and what the correction does next (SR-222's F4, R7).
 *
 * survey-api checks a correction in a fixed order (the precondition, the body, existence, the
 * status, the revision, then the writes) and answers each with its own `type`; this switches on
 * that slug and, for `writes-rejected`, on each rejection's `reason` — never on a `title`, which is
 * English that may change. What it answers is a decision as well as a sentence, because three of
 * the answers mean the page is out of date and must be read again:
 *
 * - `reason`: a 400 about the reason, said **inside the dialog**, which stays open (K6);
 * - `conflict`: a 409 `revision-conflict` — the caller re-reads and compares the answer's stored
 *   value before choosing between `conflictSentence`'s two sentences, since only the re-read can
 *   say whether somebody changed *this* answer or another one;
 * - `said`: everything else, said beside the answer with the draft kept, and `reread` where the
 *   page is out of date (the status moved, the response went, or no answer arrived).
 *
 * The rejected alternative was `refusalMessage` alone, as the other admin writes use: it would say
 * survey-api's title for a conflict ("The response changed since the revision the client last
 * saw.") and leave the person to reload and retype — and a `constraint` rejection carries only the
 * address, so the oversight surface names no finding for it (it runs no engine) and says only that
 * one would be raised.
 */
export type CorrectionRefusal =
  | { kind: 'reason'; sentence: string }
  | { kind: 'conflict' }
  | { kind: 'said'; sentence: string; reread: boolean };

export function correctionRefusal(error: unknown): CorrectionRefusal {
  // An answer that never arrived — the connection dropped, or survey-api failed — is not a
  // refusal: the correction may have landed (the hub's `unanswered` rule).
  if (!(error instanceof ApiError) || error.status >= 500) {
    return {
      kind: 'said',
      sentence: 'The answer did not arrive — check whether the value changed before saving again.',
      reread: true
    };
  }

  if (error.isProblem(ProblemType.revisionConflict)) return { kind: 'conflict' };

  if (error.isProblem(ProblemType.responseNotWritable)) {
    // The response's status rides in an extension named `status`, which replaces ProblemDetails'
    // numeric one in the parsed body (`IProblemDetails.status`).
    const status = error.problem?.status;
    return {
      kind: 'said',
      sentence:
        typeof status === 'string'
          ? `This response is ${status} now and cannot be corrected.`
          : 'This response cannot be corrected now.',
      reread: true
    };
  }

  if (error.isProblem(ProblemType.writesRejected)) {
    const reasons = [...new Set((error.problem?.rejections ?? []).map((r) => r.reason))];
    return {
      kind: 'said',
      sentence:
        reasons.length > 0
          ? reasons.map(rejectionText).join(' ')
          : 'The correction was refused.',
      reread: false
    };
  }

  if (error.isProblem(ProblemType.preconditionRequired)) {
    return {
      kind: 'said',
      sentence:
        'This page sent the correction without the response’s revision — a fault in the page, ' +
        'not in the answer. Reload and try again.',
      reread: false
    };
  }

  if (error.status === 404) {
    return { kind: 'said', sentence: 'This response is no longer there.', reread: true };
  }

  // A bodiless 403: the account's role no longer admits the write — the client's forbidden
  // handler re-reads the session's permissions for that.
  if (error.status === 403) {
    return {
      kind: 'said',
      sentence: 'This account may not correct it (403).',
      reread: false
    };
  }

  // A 400 at `reason` belongs to the dialog's box; one at `writes[n]` to the answer. Both are said
  // as every other admin 400 is — the problem's target and survey-api's reason.
  const problems = error.problem?.problems ?? [];
  const sentence = refusalMessage(error, 'The correction was refused.');
  if (problems.some((problem) => problem.target === 'reason')) return { kind: 'reason', sentence };
  return { kind: 'said', sentence, reread: false };
}

/**
 * One rejection's reason, as a sentence with the reason's spelling beside it — survey-api's matrix
 * (`AnswerWriteService`) and its finding rule. A reason this table does not know is said as
 * spelled rather than dropped.
 */
export function rejectionText(reason: string): string {
  if (reason === 'constraint') return CONSTRAINT;
  const said = REJECTIONS[reason];
  return said ? `${said} (${reason}).` : `It was refused (${reason}).`;
}

/** A hard finding the correction would introduce on the field it wrote. The rejection carries only
 *  the address, and this surface names no finding — the engine does not run here. */
const CONSTRAINT =
  'It was refused: the answer would raise a blocking finding on this field.';

const REJECTIONS: Record<string, string> = {
  unknown_usage: 'That question is not in this response’s version',
  computed_usage: 'That answer is computed and is not written by hand',
  missing_instance: 'That question sits in a repeat row, and the correction named none',
  value_shape: 'That value is not the kind this question takes',
  value_too_large: 'That text is too long to store',
  unknown_option: 'That option is not in this version’s list',
  duplicate_option: 'That option is chosen twice',
  exclusive_combo: 'That option cannot be chosen together with the others',
  trigger_key: 'That answer is addressed at an option it does not belong to',
  unknown_instance: 'That repeat row is not an active row of this response',
  // SR-416 (F7): a table's computed, N/A or heading cell — `AnswerWriteService`'s read-only cell.
  cell_read_only: 'This cell is worked out from others, or has no answer to give'
};

/**
 * Why a list's answer cell was not corrected (SR-403) — survey-api's `refused` code on one cell of
 * a *Save data* batch or a mass update, its corrections design §10 — as a sentence with the code
 * beside it, the matrix's codes and `constraint` in `rejectionText`'s words, since a cell is refused
 * by the same correction path the Answers tab writes through. A code this table does not know is
 * said as spelled.
 */
export function refusedCellText(code: string): string {
  const said = CELL_REFUSALS[code];
  if (said) return `${said} (${code}).`;
  return rejectionText(code);
}

const CELL_REFUSALS: Record<string, string> = {
  draft:
    'Not saved: only a submitted or locked response’s answers are corrected, and this one is a ' +
    'draft or no longer the record',
  hidden: 'Not saved: the response’s logic hides this answer',
  row_refused:
    'Not saved: another answer of this response was refused, and the response’s correction with it',
  cell_read_only: 'Not saved: this cell is worked out from others, or has no answer to give'
};

/** A refusal code in the fewest words — a mass update's count of each ("2 drafts"); a code no
 *  table words is spelled. A mass update over responses pinned to different versions meets the
 *  matrix's codes, each row checked against its own version. */
export function refusedCodeShort(code: string, count: number): string {
  const n = count.toLocaleString('en-US');
  if (code === 'draft') return `${n} ${count === 1 ? 'draft' : 'drafts'}`;
  return `${n} ${REFUSALS_SHORT[code] ?? code}`;
}

const REFUSALS_SHORT: Record<string, string> = {
  hidden: 'hidden by the response’s logic',
  constraint: 'that would raise a blocking finding',
  row_refused: 'with another answer refused',
  unknown_usage: 'whose version lacks the question',
  computed_usage: 'computed',
  value_shape: 'whose version takes another kind of value',
  value_too_large: 'too long to store',
  unknown_option: 'whose version lacks the option',
  duplicate_option: 'with an option chosen twice',
  exclusive_combo: 'with options that cannot go together',
  cell_read_only: 'cells that are worked out, N/A or a heading'
};

/**
 * What a 409 `revision-conflict` is said as, once the caller has read the response again (R7).
 * `now` is the answer's stored value after the re-read, drawn as the tab draws it, or `null` when
 * this answer did not change; `by` is who changed it and when, from the newest row of the
 * question's history at this address, when one was found. Either way the correction was not sent,
 * and the re-read's `ETag` is the one the next Save quotes — so nobody's value is overwritten
 * unseen.
 */
export function conflictSentence(now: Nullable<string>, by: Nullable<string>): string {
  if (now === null) {
    return (
      'The response changed since you opened it, elsewhere — your correction was not sent. ' +
      'Save again to send it over the current answers.'
    );
  }
  return (
    `This answer changed since you opened it — now ${now}${by ? ` (${by})` : ''}. ` +
    'Your correction was not sent.'
  );
}
